/**
 * Which links the palette may offer between two selected elements.
 *
 * The server decides; this decides what the therapist is allowed to *reach
 * for*. Illegal options are returned disabled with a reason rather than
 * dropped: a palette that silently hides what it will not do teaches nothing,
 * and the reason is where the notation gets taught.
 */

import type { LinkType, MapLink, MapNode } from "./types";

/**
 * What else is on the board, when the answer depends on it.
 *
 * Children hang from a couple's line rather than from a parent, which is what
 * puts siblings on one sibship line. Strictly, that means picking the union
 * and then the child — and in a session nobody does that: they point at the
 * parent and at the child. So when the parent is in exactly one union, that
 * union is the one meant and the palette says yes. When they are in two, it
 * genuinely is ambiguous and the palette says which choice is missing.
 */
export interface BoardContext {
    links: MapLink[];
}

export interface LinkOption {
    type: LinkType;
    enabled: boolean;
    /** i18n key explaining why not. Present only when disabled. */
    reasonKey?: string;
}

export type Selectable = MapNode | MapLink | null;

const isPerson = (element: Selectable): boolean =>
    Boolean(element && "type" in element && element.type === "person");

const isUnion = (element: Selectable): boolean =>
    Boolean(element && "type" in element && element.type === "union");

const bothPeople = (a: Selectable, b: Selectable) => isPerson(a) && isPerson(b);

/** True when a parent has more than one couple, so which one is a real question. */
const ambiguousParent = (a: Selectable, b: Selectable, links: MapLink[]): boolean =>
    [a, b].some((element) => isPerson(element) && unionsOf(element, links).length > 1);

/** A union and a person, in either order — the therapist may click either first. */
const unionAndPerson = (a: Selectable, b: Selectable) =>
    (isUnion(a) && isPerson(b)) || (isPerson(a) && isUnion(b));

/** The unions a person belongs to. */
export const unionsOf = (person: Selectable, links: MapLink[]): MapLink[] =>
    person
        ? links.filter(
              (link) =>
                  link.type === "union" &&
                  (link.from === person.id || link.to === person.id),
          )
        : [];

/**
 * The couple a child link drawn between these two would hang from.
 *
 * Either one of them *is* the union, or one of them is a parent with a single
 * union — anything else is a guess, and the board does not guess about who
 * someone's parents are.
 */
export const unionBehind = (
    a: Selectable,
    b: Selectable,
    links: MapLink[],
): MapLink | null => {
    // A child link joins a couple to a *person*. Two unions are two couples,
    // and neither is anyone's child.
    if (isUnion(a) && isPerson(b)) return a as MapLink;
    if (isUnion(b) && isPerson(a)) return b as MapLink;

    for (const [parent, child] of [
        [a, b],
        [b, a],
    ] as [Selectable, Selectable][]) {
        if (!isPerson(parent) || !isPerson(child)) continue;
        const unions = unionsOf(parent, links).filter(
            (union) => union.from !== child?.id && union.to !== child?.id,
        );
        if (unions.length === 1) return unions[0];
    }
    return null;
};

export const genogramLinkOptions = (
    a: Selectable,
    b: Selectable,
    context: BoardContext = { links: [] },
): LinkOption[] => {
    const distinct = Boolean(a && b && a.id !== b.id);
    const people = distinct && bothPeople(a, b);
    const filiation =
        distinct &&
        (unionAndPerson(a, b) || Boolean(unionBehind(a, b, context.links)));

    return [
        {
            type: "union",
            enabled: people,
            reasonKey: people ? undefined : "systemicMap.rules.unionNeedsTwoPeople",
        },
        {
            type: "emotional",
            enabled: people,
            reasonKey: people ? undefined : "systemicMap.rules.emotionalNeedsTwoPeople",
        },
        {
            type: "child",
            enabled: filiation,
            // Children hang off the couple's line rather than off each parent,
            // which is what puts siblings on one sibship line.
            reasonKey: filiation
                ? undefined
                : ambiguousParent(a, b, context.links)
                  ? "systemicMap.rules.childNeedsWhichUnion"
                  : "systemicMap.rules.childNeedsUnion",
        },
    ];
};
